import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { marketDesignFieldSchema } from './_validators';
import FormGeneral from './components/common/FormGeneral';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Error } from '@/components/ui/error';

import { usePutMarketDesignField, useMarketDesignFieldObject } from '@/data/marketDesignField';

const EditMarketDesignFieldDialog = ({ uid, onClose, onSuccess }) => {
  const params = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const putMarketDesignField = usePutMarketDesignField();
  const marketDesignField = useMarketDesignFieldObject(params.sectorTypeUid, uid);

  const form = useForm({
    resolver: zodResolver(marketDesignFieldSchema),
    defaultValues: {
      code: marketDesignField.code,
      label: marketDesignField.label,
      description: marketDesignField.description,
      value_unit_label: marketDesignField.value_unit_label || 'None',
      value_unit_decimals: marketDesignField.value_unit_decimals.toString()
    }
  });

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      setApiError('');
      const payload = {
        sector_type_uid: params.sectorTypeUid,
        code: values.code,
        label: values.label,
        value_unit_label: values.value_unit_label === 'None' ? '' : values.value_unit_label,
        value_unit_decimals: parseInt(values.value_unit_decimals),
        description: values.description
      };
      const updatedMarketDesignField = await putMarketDesignField(marketDesignField.uid, payload);
      await onSuccess(updatedMarketDesignField);
      onClose();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <DialogHeader>
              <DialogTitle>Edit Market Design Field</DialogTitle>
              <DialogDescription>Update market design field details</DialogDescription>
            </DialogHeader>
            <FormGeneral />
            {apiError && <Error message={apiError} />}
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin" />}Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMarketDesignFieldDialog;
